
// @ts-ignore
    import __i18nConfig from '@next-translate-root/i18n'
// @ts-ignore
    import __loadNamespaces from 'next-translate/loadNamespaces'
// @ts-ignore
    
import React, { useMemo } from 'react';
import { addApolloState, initializeApollo } from '@lib/apollo';
import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import { css } from '@emotion/react';
import theme from '@styles/theme';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import PlayTypeProvider from '@/provider/PlayTypeProvider';
import { HOME_TAB_TYPE } from '@customTypes/home';
import { Story_Play_Type, Story_Sorting } from '@/baseType';

import HomeBannerWrapper from '@/components/main/common/banner/HomeBannerWrapper';
import HomeContainer from '@/components/main/home/HomeContainer';
import WeekDayContainer from '@/components/main/home/weekday/WeekDayContainer';
import AdultContainer from '@/components/main/home/adultTab';
import GenreContainer from '@/components/main/home/genreTab';
import { useInteractiveStoryGenresQuery } from '@/operations/queries/home/__generated__/useInteractiveStoryGenreList.generated';
import { useGetUserQuery } from '@/operations/queries/user/__generated__/getUser.generated';
import { ADULT_TAB, TABS_WITHOUT_ADULT } from '@/common/values';
import {
  STORY_PLAY_TAB_TYPES,
  StoryPlayTabType,
} from '@/lib/common-react/types/storyGame';
import { SPHeader } from '@/lib/common-react/components/header/SPHeader';
import useTranslation from 'next-translate/useTranslation';
import { touchSearchTab } from '@/lib/googleAnalytics';
import { getCurrentWeekType } from '@/common/utils';
import { useGetTopHeaderTabs } from '@/hooks/useGetTopHeaderTabs';

const InteractivePage = () => {
  const { t } = useTranslation();
  const router = useRouter();
  const { tab = HOME_TAB_TYPE.Home, genre } = router.query;
  const { data: userData } = useGetUserQuery({});
  const { data: genreData } = useInteractiveStoryGenresQuery();
  const { topHeaderTabs } = useGetTopHeaderTabs();

  const categoryList = useMemo(() => {
    const tabsWithOutAdult = TABS_WITHOUT_ADULT.map((i) => {
      return { type: i.type, title: t(i.text.key) || i.text.ko_KR };
    });
    const adultTab = {
      type: ADULT_TAB.type,
      title: t(ADULT_TAB.text.key) || ADULT_TAB.text.ko_KR,
    };
    const genreTabs = genreData?.interactiveStoryGenresV3?.map((i) => {
      return { type: i.type, title: t(i.text.key) || i.text.ko_KR };
    });
    if (
      userData?.getUser?.certificatedAt &&
      !userData.getUser.isExpiredCertification
    ) {
      return [...tabsWithOutAdult, adultTab, ...(genreTabs || [])];
    } else {
      return [...tabsWithOutAdult, ...(genreTabs || []), adultTab];
    }
  }, [genreData, t, userData]);

  const handleTabClick = ({ type }: { type: StoryPlayTabType }) => {
    switch (type) {
      case STORY_PLAY_TAB_TYPES.interactive:
        router.push('/interactive');
        break;
      case STORY_PLAY_TAB_TYPES.storyGame:
        router.push('/home/storygame');
        break;
      case STORY_PLAY_TAB_TYPES.webNovel:
        router.push('/webnovel');
        break;
      case STORY_PLAY_TAB_TYPES.challenge:
        router.push(
          '/challenge?genre=TOTAL&sorting=RECOMMENDED&showFilter=true',
        );
        break;
      default:
        break;
    }
  };

  const handleCategoryClick = ({
    type,
  }: {
    type: string; // Genre | Recommended | HOME | WEEK_DAY | ADULT
    genre?: string | null;
  }) => {
    router.push({
      pathname: router.pathname,
      query: {
        tab: type,
        ...(type === 'WEEK_DAY' && {
          week: getCurrentWeekType(),
          sorting: Story_Sorting.DailyRankAsc,
          genre: 'TOTAL',
        }),
      },
    });
  };

  const handleMainLogoIconClick = () => {
    router.push('/interactive');
  };

  const handleStoreIconClick = () => {
    router.push('/store');
  };

  const handleProfileIconClick = () => {
    router.push('/more');
  };

  const handleSearchIconClick = () => {
    router.push('/search');
    touchSearchTab();
  };

  const handleNotificationIconClick = () => {
    router.push('/notification');
  };

  return (
    <PlayTypeProvider playType={Story_Play_Type.Interactive}>
      <div css={container}>
        <SPHeader
          data={{
            tabList: topHeaderTabs,
            currentTabType: STORY_PLAY_TAB_TYPES.interactive,
            categoryList,
            currentCategoryType: (tab as string) || '',
            currentCategoryGenre: genre as string,
          }}
          actions={{
            onLogoClick: handleMainLogoIconClick,
            onTabClick: handleTabClick,
            onCategoryClick: handleCategoryClick,
            onStoreIconClick: handleStoreIconClick,
            onSearchIconClick: handleSearchIconClick,
            onProfileIconClick: handleProfileIconClick,
            onNotificationIconClick: handleNotificationIconClick,
          }}
          isDarkTheme={false}
          maxWidth={theme.maxWidth}
          tab="interactive"
        />
        <main className="main-container">
          {tab === HOME_TAB_TYPE.Home && (
            <>
              <HomeBannerWrapper />
              <HomeContainer playType={Story_Play_Type.Interactive} />
            </>
          )}
          {tab === HOME_TAB_TYPE.WeekDay && (
            <div className="weekday-wrapper">
              <WeekDayContainer />
            </div>
          )}
          {tab === HOME_TAB_TYPE.Adult && (
            <div className="adult-wrapper">
              <AdultContainer playType={Story_Play_Type.Interactive} />
            </div>
          )}
          {/* 장르 탭 */}
          {tab !== HOME_TAB_TYPE.Home &&
            tab !== HOME_TAB_TYPE.WeekDay &&
            tab !== HOME_TAB_TYPE.Adult && (
              <div className="genre-wrapper">
                <GenreContainer
                  playType={Story_Play_Type.Interactive}
                  genreType={tab as string}
                  genreName={
                    genreData?.interactiveStoryGenresV3.find(
                      (item) => item.type === (tab as string),
                    )?.text.ko_KR as string
                  }
                />
              </div>
            )}
        </main>
      </div>
    </PlayTypeProvider>
  );
};

const container = css`
  width: 100%;
  max-width: ${theme.maxWidth};
  display: flex;
  flex-direction: column;
  align-items: center;

  .main-container {
    width: 100%;
    max-width: ${theme.maxWidth};
    min-height: 80rem;
    background: ${theme.colors.white};
  }
`;

 const _getServerSideProps: GetServerSideProps = async (context) => {
  const client = initializeApollo();

  try {
    // await client.query({
    //   query: MONTHLY_STORY_LIST,
    //   context: getContextByLocale(context.locale),
    //   variables: {
    //     playType: Story_Play_Type.Interactive,
    //   },
    // });
    // await client.query({
    //   query: NEW_STORY_LIST,
    //   context: getContextByLocale(context.locale),
    // });

    return addApolloState(client, {
      props: {},
    });
  } catch (e) {
    return {
      notFound: true,
    };
  }
};

export default InteractivePage;


// @ts-ignore
    export async function getServerSideProps(ctx) {
// @ts-ignore
        let res = _getServerSideProps(ctx)
// @ts-ignore
        if(typeof res.then === 'function') res = await res
// @ts-ignore
        return {
// @ts-ignore
          
// @ts-ignore
          ...res,
// @ts-ignore
          props: {
// @ts-ignore
            ...(res.props || {}),
// @ts-ignore
            ...(await __loadNamespaces({
// @ts-ignore
              ...ctx,
// @ts-ignore
              pathname: '/interactive',
// @ts-ignore
              loaderName: 'getServerSideProps',
// @ts-ignore
              ...__i18nConfig,
// @ts-ignore
              loadLocaleFrom: (l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default),
// @ts-ignore
            }))
// @ts-ignore
          }
// @ts-ignore
        }
// @ts-ignore
    }
// @ts-ignore
  